import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  clampMiddleCarouselPageIndex,
  getMiddleCarouselBudget,
  getMiddleCarouselNeedsNav,
  getMiddleCarouselPageCount,
  getMiddleCarouselPageSlice,
} from '../glassWidgetCarouselMath';

import './_glassWidgetCardCarousel.scss';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const MIDDLE_CAROUSEL_AUTO_ADVANCE_MS = 12_000;

export interface GlassWidgetCardCarouselProps {
  /** Use `Number.POSITIVE_INFINITY` for tall viewports (show all cards, no middle pagination). */
  maxVisible: number;
  pinnedTop: ReactNode[];
  pinnedBottom: ReactNode[];
  middleCards: ReactNode[];
}

export function GlassWidgetCardCarousel({
  maxVisible,
  pinnedTop,
  pinnedBottom,
  middleCards,
}: GlassWidgetCardCarouselProps) {
  const [middlePage, setMiddlePage] = useState(0);
  const [middleCarouselPaused, setMiddleCarouselPaused] = useState(false);

  const pinnedSlots = pinnedTop.length + pinnedBottom.length;
  const middleBudget = useMemo(
    () => getMiddleCarouselBudget(maxVisible, pinnedSlots),
    [maxVisible, pinnedSlots],
  );
  const pageCount = useMemo(
    () => getMiddleCarouselPageCount(middleCards.length, middleBudget),
    [middleCards.length, middleBudget],
  );

  const needsMiddleNav = useMemo(
    () => getMiddleCarouselNeedsNav(maxVisible, pinnedSlots, middleCards.length),
    [maxVisible, pinnedSlots, middleCards.length],
  );

  const effectiveMiddlePage = useMemo(
    () => clampMiddleCarouselPageIndex(middlePage, pageCount),
    [middlePage, pageCount],
  );

  const visibleMiddle = useMemo(
    () => getMiddleCarouselPageSlice(middleCards, effectiveMiddlePage, middleBudget),
    [middleCards, effectiveMiddlePage, middleBudget],
  );

  const goPrev = useCallback(() => {
    setMiddlePage((p) => {
      const cur = clampMiddleCarouselPageIndex(p, pageCount);
      return cur <= 0 ? pageCount - 1 : cur - 1;
    });
  }, [pageCount]);

  const goNext = useCallback(() => {
    setMiddlePage((p) => {
      const cur = clampMiddleCarouselPageIndex(p, pageCount);
      return cur >= pageCount - 1 ? 0 : cur + 1;
    });
  }, [pageCount]);

  useEffect(() => {
    if (!needsMiddleNav || middleCarouselPaused) return undefined;
    const id = window.setInterval(() => {
      goNext();
    }, MIDDLE_CAROUSEL_AUTO_ADVANCE_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [needsMiddleNav, middleCarouselPaused, goNext]);

  const prevAria = 'Previous widget cards';
  const nextAria = 'Next widget cards';
  const middleRegionAria = 'Additional home widgets';

  const middlePageStatusText = useMemo(
    () => `Page ${effectiveMiddlePage + 1} of ${pageCount}`,
    [effectiveMiddlePage, pageCount],
  );

  return (
    <>
      {pinnedTop}
      <div
        className="glass-widget-card-carousel__middle"
        role={needsMiddleNav ? 'region' : undefined}
        aria-label={needsMiddleNav ? middleRegionAria : undefined}
        onMouseEnter={() => {
          setMiddleCarouselPaused(true);
        }}
        onMouseLeave={() => {
          setMiddleCarouselPaused(false);
        }}
      >
        <div className="glass-widget-card-carousel__middle-stack">
          {needsMiddleNav ? (
            <span className="glass-widget-card-carousel__sr-only" aria-live="polite" aria-atomic="true">
              {middlePageStatusText}
            </span>
          ) : null}
          {visibleMiddle}
          {needsMiddleNav ? (
            <>
              <button
                className="glass-widget-card-carousel__nav-btn glass-widget-card-carousel__nav-btn--prev"
                type="button"
                onClick={goPrev}
                aria-label={prevAria}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left' as IconName]} />
              </button>
              <button
                className="glass-widget-card-carousel__nav-btn glass-widget-card-carousel__nav-btn--next"
                type="button"
                onClick={goNext}
                aria-label={nextAria}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right' as IconName]} />
              </button>
            </>
          ) : null}
        </div>
      </div>
      {pinnedBottom}
    </>
  );
}
